import throttle from "lodash/throttle";

import {
  handleApplicationCreatedEvent,
  handleApplicationOffers,
  handleApplicationScoreCardGroupResultsFinishedEvent,
  handleApplicationScoreCardResultsFinishedEvent,
  handleApplicationUpdatedEvent,
  handleCheckoutProgressUpdated,
  handleCheckoutSnapshotSaved,
  handleDataOrchestrationLogEvent,
  handleDocuSignEvent,
  handleEnrichmentResponseUpdatedEvent,
  handleExecuteDataOrchestrationBatchFinishedEvent,
  handleExternalServiceLogEvent,
  handlePlacementStage,
  handlePlaidEvent,
  handleStageTransitioned,
} from "@/helpers/websockets/applications/handlers";

import type {
  IApplication,
  IApplicationPlaidSummary,
} from "@/models/applications";
import type { CheckoutProgress, CheckoutSnapshot } from "@/models/checkout";

export interface ExternalServiceLogEvent {
  applicationId: string;
  serviceId: string;
}

export interface DataOrchestrationLogEvent {
  applicationId: string;
  logId: string;
}

export interface DocuSignEvent {
  id: string;
  status: number;
}

export interface ApplicationCreatedEvent {
  id: IApplication;
}

export interface ApplicationScoreCardGroupResultsFinishedEvent {
  application_id: string;
}

export interface ApplicationScoreCardResultsFinishedEvent {
  application_id: string;
}

export interface OfferAcceptedEvent {
  offer_id: string;
  application_id: string;
}

export interface PlaidConnectionEvent {
  application_id: string;
  plaid: IApplicationPlaidSummary;
}

export interface EnrichmentResponseUpdatedEvent {
  application_id: string;
}

export type CheckoutSnapshotSavedEvent = Omit<
  CheckoutSnapshot,
  "name" | "description"
>;
export type CheckoutProgressUpdatedEvent = Omit<
  CheckoutProgress,
  "started_at" | "completed_at" | "created_at" | "updated_at"
>;

export const EXTERNAL_SERVICE_LOG_EVENT = "ExternalServiceLogEvent" as const;
export const DATA_ORCHESTRATION_SERVICE_LOG_EVENT =
  "DataOrchestrationLogEvent" as const;
export const APPLICATION_CREATED_EVENT = "ApplicationCreatedEvent" as const;
export const APPLICATION_UPDATED_EVENT =
  "Application\\ApplicationWasUpdated" as const;
export const OFFER_ACCEPTED_EVENT =
  "BorrowerPlatform\\OfferAcceptedEvent" as const;
export const APPLICATION_SCORECARD_GROUP_RESULTS_FINISHED_EVENT =
  "ScoreCardGroupResultsFinishedEvent" as const;
export const APPLICATION_SCORECARD_RESULTS_FINISHED_EVENT =
  "ScoreCardResultsFinishedEvent" as const;
export const DOCU_SIGN_EVENT = "DocuSignEvent" as const;
export const EXECUTE_DATA_ORCHESTRATION_BATCH_FINISHED_EVENT =
  "Application\\ExecuteDataOrchestrationFinished" as const;
export const PLAID_CONNECTION_EVENT = "PlaidConnectionEvent" as const;

export const ENRICHMENT_RESPONSE_UPDATED_EVENT =
  "EnrichmentResponseUpdatedEvent" as const;

export const CHECKOUT_SNAPSHOT_SAVED = ".checkout_snapshot_saved" as const;
export const CHECKOUT_PROGRESS_UPDATED = ".checkout_progress_updated" as const;

export const PLACEMENT_STAGE = ".placement_stage" as const;

export const STAGE_TRANSITIONED = ".stage_transitioned" as const;

export const APPLICATIONS_EVENT_HANDLERS = {
  [EXTERNAL_SERVICE_LOG_EVENT]: handleExternalServiceLogEvent,
  [OFFER_ACCEPTED_EVENT]: handleApplicationOffers,
  [DATA_ORCHESTRATION_SERVICE_LOG_EVENT]: handleDataOrchestrationLogEvent,
  [DOCU_SIGN_EVENT]: handleDocuSignEvent,
  [APPLICATION_SCORECARD_GROUP_RESULTS_FINISHED_EVENT]:
    handleApplicationScoreCardGroupResultsFinishedEvent,
  [APPLICATION_SCORECARD_RESULTS_FINISHED_EVENT]:
    handleApplicationScoreCardResultsFinishedEvent,
  [APPLICATION_UPDATED_EVENT]: handleApplicationUpdatedEvent,
  [PLAID_CONNECTION_EVENT]: handlePlaidEvent,
  [ENRICHMENT_RESPONSE_UPDATED_EVENT]: handleEnrichmentResponseUpdatedEvent,
  [PLACEMENT_STAGE]: handlePlacementStage,
  [CHECKOUT_SNAPSHOT_SAVED]: handleCheckoutSnapshotSaved,
  [CHECKOUT_PROGRESS_UPDATED]: handleCheckoutProgressUpdated,
  [STAGE_TRANSITIONED]: handleStageTransitioned,
} as const;

export const APPLICATION_LIST_HANDLERS = {
  [APPLICATION_CREATED_EVENT]: throttle(handleApplicationCreatedEvent, 10000),
  [EXECUTE_DATA_ORCHESTRATION_BATCH_FINISHED_EVENT]:
    handleExecuteDataOrchestrationBatchFinishedEvent,
} as const;
